import { Csku, ErrorBoundary } from "@commonsku/styles";
import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getPopups } from "../../selectors";
import createPopup from "../../popup-factory";
import NoMatch from "../NoMatch";
import { closePopup, fetchNotes, getClientDetailsState } from "../../redux/clientDetails";
import { ClientSidebar } from "./ClientSidebar";
import Header from "../../containers/Header";
import MainSection from "../../containers/MainSection";
import { OverviewPage } from "./Overview/OverviewPage";
import styled from "styled-components";
import { popups } from "./types";
import { toast, ToastContainer } from "react-toastify";
import { oauth } from "../../utils";
import { ContactsPage } from "./Contacts/ContactsPage";
import { AddressesPage } from "./Addresses/AddressesPage";
import { ActivityPage } from "./Activity/ActivityPage";
import useClientDetails from "./hooks/useClientDetails";
import { ProjectsPage } from "./Projects/ProjectsPage";
import { useIdentity } from "../../hooks";
import { getIdentityUtils } from "../../utils";
import { createCreditHoldPopup } from "../../actions/popup";
import { LibraryPage } from "./Library/LibraryPage";
import { CatalogPage } from "./Catalog/CatalogPage";
import { CatalogItemEditContext } from "./Catalog/CatalogItemEditContext";
import CreateShop from "../shop/Creation/CreateShop";

// Refetch notes every 5 minutes
const REFETCH_NOTES_INTERVAL = 300 * 1000;
const MINIMUM_NOTES_TO_FETCH = 4;

const Home = styled.div`
    &&& {
        display: flex;
        height: 100%;
        width: 100%;
        padding: 32px;
        padding-top: calc(32px - 1rem);
        padding-left: calc(32px - 1rem);
    }
`;

export const ClientHome = () => {
    const [isCatalogItemEditing, setCatalogItemEditing] = useState(false);
    const basePopups = useSelector(getPopups);
    const { client, contacts, users, addresses, shippingAccounts } = useClientDetails();
    const { notes, currentPopup } = useSelector(getClientDetailsState);
    const dispatch = useDispatch();
    const identity = useIdentity();
    const { user_id } = identity;
    const { hasCapabilities, hasUserFlags } = getIdentityUtils(identity);
    const canViewCatalog =
        hasCapabilities(["HAS-CLIENT-CATALOG", "VIEW-CATALOG-ITEM"]) &&
        hasUserFlags(["CLIENT-CATALOG"]);
    const isCreateShop = useSelector((state: any) => state.temp?.create_shop);

    useEffect(() => {
        document.title = client.name;
    }, [client.name]);

    useEffect(() => {
        let timerId: ReturnType<typeof setInterval> | null = null;
        const fetch = async () => {
            try {
                await dispatch(fetchNotes({
                    user_id,
                    client_id: client.id,
                    users,
                    contacts,
                    minimumNotes: MINIMUM_NOTES_TO_FETCH,
                    getNewNotes: true,
                }));
            } catch (e) {
                if (timerId !== null) {
                    clearInterval(timerId);
                    timerId = null;
                }
            }
        };

        fetch();
        timerId = setInterval(fetch, REFETCH_NOTES_INTERVAL);
        return () => {
            if (timerId !== null) {
                clearInterval(timerId);
            }
        };
    }, []);

    useEffect(() => {
        if (client.creditHold) {
            dispatch(createCreditHoldPopup());
        }
    }, [client.creditHold]);

    const menu = <ClientSidebar />;

    const onDeleteClient = () => {
        oauth('DELETE', `account/${client.id}`, { company_type: "CLIENT" })
            .then(() => {
                toast.success('Client deleted');
                // Can't use react-router navigation outside of react
                setTimeout(() => window.location.href = '../clients.php', 500);
            })
            .catch(() => {
                toast.error('Error deleting client');
            });
    };

    const renderPopup = () => {
        if (!currentPopup) return null;

        const popupProps = {
            onClosePopup: () => dispatch(closePopup()),
            onClose: () => dispatch(closePopup()),
            client_id: client.id,
            parent_id: client.id,
            parent_type: 'CLIENT',
        };

        const Popup = popups[currentPopup];

        switch (currentPopup) {
            case "delete-client":
                return (
                    <Popup
                        onClose={popupProps.onClose}
                        onDelete={onDeleteClient}
                    />
                );
            default:
                return <Popup {...popupProps} />;
        }
    };

    return (
        <CatalogItemEditContext.Provider
            value={{ isCatalogItemEditing, setCatalogItemEditing }}
        >
        <Header menu={menu} />
        <MainSection menu={menu}>
            {renderPopup()}
            {isCreateShop ? <CreateShop /> : null}
            {!isCatalogItemEditing && basePopups
                ? basePopups.map((p, idx) => createPopup(p, idx))
                : null}
            <Csku as={"div"} className="main-content">
                <Home>
                    <Routes>
                        <Route
                            index
                            element={
                                <OverviewPage
                                    client={client}
                                    contacts={contacts}
                                    recentActivity={notes ?? []}
                                />
                            }
                        />
                        <Route
                            path="/activity"
                            element={<ActivityPage client={client} users={users} contacts={contacts} />}
                        />
                        <Route
                            path="/contacts"
                            element={<ContactsPage client={client} contacts={contacts} />}
                        />
                        <Route
                            path="/addresses"
                            element={<AddressesPage client={client} addresses={addresses} shippingAccounts={shippingAccounts} />}
                        />
                        <Route
                            path="/library"
                            element={<LibraryPage client={client} />}
                        />
                        <Route
                            path="/projects"
                            element={<ProjectsPage client={client} />}
                        />
                        <Route
                            path="/catalog"
                            element={
                                canViewCatalog
                                    ? <CatalogPage client={client} />
                                    : <NoMatch location={window.location} />
                            }
                        />
                        <Route
                            path={"*"}
                            element={<NoMatch location={window.location} />}
                            ErrorBoundary={ErrorBoundary}
                        />
                    </Routes>
                </Home>
                <ToastContainer
                    autoClose={3000}
                    hideProgressBar={true}
                />
            </Csku>
        </MainSection>
        </CatalogItemEditContext.Provider>
    );
};
